import { useState, useEffect, useMemo } from 'react';
import {
  Package,
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  Download,
  Star,
  Eye,
  ThumbsUp,
  ThumbsDown,
  AlertTriangle,
  Plus,
  X,
  Edit,
  Upload,
  RefreshCw,
  FileJson,
  Cloud,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAdminAuth } from '@/services/adminAuth';
import {
  type FirestoreExtension,
  onExtensionsChange,
  addExtension as firebaseAddExtension,
  updateExtension as firebaseUpdateExtension,
  deleteExtension as firebaseDeleteExtension,
  approveExtension as firebaseApproveExtension,
  rejectExtension as firebaseRejectExtension,
} from '@/services/firebase/adminFirestore';
import { exportExtensionsJSON, generateExtensionsData } from '@/api/extensions';

type ExtensionStatus = 'approved' | 'pending' | 'rejected';
type ExtensionCategory = 'Languages' | 'Themes' | 'SCM' | 'AI' | 'DevOps' | 'Database' | 'Snippets' | 'Debuggers' | 'Formatters' | 'Other';

const categories: ExtensionCategory[] = ['Languages', 'Themes', 'SCM', 'AI', 'DevOps', 'Database', 'Snippets', 'Debuggers', 'Formatters', 'Other'];

export function AdminExtensions() {
  const { session } = useAdminAuth();

  // Firebase state
  const [extensions, setExtensions] = useState<FirestoreExtension[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedExtension, setSelectedExtension] = useState<FirestoreExtension | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingExtension, setEditingExtension] = useState<FirestoreExtension | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Real-time Firebase listener
  useEffect(() => {
    setIsLoading(true);
    setError(null);

    const unsubscribe = onExtensionsChange((fetchedExtensions) => {
      setExtensions(fetchedExtensions);
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Calculate stats from extensions
  const stats = useMemo(() => {
    const total = extensions.length;
    const approved = extensions.filter(e => e.status === 'approved').length;
    const pending = extensions.filter(e => e.status === 'pending').length;
    const rejected = extensions.filter(e => e.status === 'rejected').length;
    const totalDownloads = extensions.reduce((sum, e) => sum + (e.downloads || 0), 0);
    return { total, approved, pending, rejected, totalDownloads };
  }, [extensions]);

  // Form state
  const [formName, setFormName] = useState('');
  const [formPublisher, setFormPublisher] = useState('');
  const [formVersion, setFormVersion] = useState('1.0.0');
  const [formDescription, setFormDescription] = useState('');
  const [formCategory, setFormCategory] = useState<ExtensionCategory>('Other');
  const [formTags, setFormTags] = useState('');
  const [formStatus, setFormStatus] = useState<ExtensionStatus>('pending');
  const [formChangelog, setFormChangelog] = useState('');

  const filteredExtensions = extensions.filter(ext => {
    const matchesSearch = ext.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ext.publisher.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ext.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-500">
          <CheckCircle2 size={10} /> Approved
        </span>;
      case 'pending':
        return <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-500">
          <Clock size={10} /> Pending Review
        </span>;
      case 'rejected':
        return <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-500">
          <XCircle size={10} /> Rejected
        </span>;
      default:
        return null;
    }
  };

  const handleApprove = async (id: string) => {
    try {
      setIsUpdating(true);
      await firebaseApproveExtension(id, session?.user.email || 'admin');
      setSelectedExtension(null);
    } catch (err) {
      console.error('Failed to approve extension:', err);
      setError('Failed to approve extension. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleReject = async (id: string) => {
    try {
      setIsUpdating(true);
      await firebaseRejectExtension(id, session?.user.email || 'admin');
      setSelectedExtension(null);
    } catch (err) {
      console.error('Failed to reject extension:', err);
      setError('Failed to reject extension. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddExtension = async () => {
    if (!formName || !formPublisher || !formDescription) return;

    try {
      setIsUpdating(true);
      await firebaseAddExtension({
        name: formName,
        publisher: formPublisher,
        version: formVersion,
        description: formDescription,
        category: formCategory,
        status: formStatus,
        tags: formTags.split(',').map(t => t.trim()).filter(Boolean),
      });

      resetForm();
      setShowAddModal(false);
    } catch (err) {
      console.error('Failed to add extension:', err);
      setError('Failed to add extension. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this extension?')) {
      try {
        setIsUpdating(true);
        await firebaseDeleteExtension(id);
        setSelectedExtension(null);
      } catch (err) {
        console.error('Failed to delete extension:', err);
        setError('Failed to delete extension. Please try again.');
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const resetForm = () => {
    setFormName('');
    setFormPublisher('');
    setFormVersion('1.0.0');
    setFormDescription('');
    setFormCategory('Other');
    setFormTags('');
    setFormStatus('pending');
    setFormChangelog('');
  };

  // Open edit modal with extension data
  const openEditModal = (ext: FirestoreExtension) => {
    setEditingExtension(ext);
    setFormName(ext.name);
    setFormPublisher(ext.publisher);
    setFormVersion(ext.version);
    setFormDescription(ext.description);
    setFormCategory(ext.category as ExtensionCategory);
    setFormTags(ext.tags?.join(', ') || '');
    setFormStatus(ext.status);
    setFormChangelog('');
    setShowEditModal(true);
  };

  // Handle extension update
  const handleUpdateExtension = async () => {
    if (!editingExtension || !formName || !formPublisher || !formDescription) return;

    setIsUpdating(true);
    try {
      await firebaseUpdateExtension(editingExtension.id, {
        name: formName,
        publisher: formPublisher,
        version: formVersion,
        description: formDescription,
        category: formCategory,
        status: formStatus,
        tags: formTags.split(',').map(t => t.trim()).filter(Boolean),
      });

      setShowEditModal(false);
      setEditingExtension(null);
      resetForm();
    } catch (err) {
      console.error('Failed to update extension:', err);
      setError('Failed to update extension. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Increment version helper
  const incrementVersion = (type: 'major' | 'minor' | 'patch') => {
    const parts = formVersion.split('.').map(Number);
    if (parts.length !== 3) {
      setFormVersion('1.0.0');
      return;
    }
    switch (type) {
      case 'major':
        setFormVersion(`${parts[0] + 1}.0.0`);
        break;
      case 'minor':
        setFormVersion(`${parts[0]}.${parts[1] + 1}.0`);
        break;
      case 'patch':
        setFormVersion(`${parts[0]}.${parts[1]}.${parts[2] + 1}`);
        break;
    }
  };

  // Export extensions to JSON file
  const handleExportJSON = () => {
    const json = exportExtensionsJSON();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ochtopus-extensions-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Sync extensions to public API (saves to localStorage for now)
  const handleSyncToAPI = () => {
    const data = generateExtensionsData();
    localStorage.setItem('ochtopus-marketplace-api', JSON.stringify({
      extensions: data,
      lastUpdated: new Date().toISOString(),
      total: data.length,
    }));
    alert(`${data.length} extensions synced to API. IDE can now fetch updated data.`);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 text-accent animate-spin" size={48} />
          <p className="text-text-secondary">Loading extensions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="text-red-500 font-medium">Error</p>
            <p className="text-sm text-red-500/70">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-500 hover:bg-red-500/10 p-1 rounded"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Extensions</h1>
          <p className="text-text-secondary">Manage marketplace extensions and submissions</p>
        </div>
        <div className="flex items-center gap-2">
          {stats.pending > 0 && (
            <span className="px-3 py-1 rounded-full bg-yellow-500/10 text-yellow-500 text-sm font-medium">
              {stats.pending} pending review
            </span>
          )}
          <button
            onClick={handleExportJSON}
            className="btn btn-secondary"
            title="Export extensions as JSON file"
          >
            <FileJson size={16} />
            Export JSON
          </button>
          <button
            onClick={handleSyncToAPI}
            className="btn bg-green-500/10 text-green-500 hover:bg-green-500/20"
            title="Sync extensions to IDE API"
          >
            <Cloud size={16} />
            Sync to IDE
          </button>
          <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
            <Plus size={16} />
            Add Extension
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <p className="text-2xl font-bold text-text-primary">{stats.total}</p>
          <p className="text-sm text-text-secondary">Total Extensions</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <p className="text-2xl font-bold text-green-500">{stats.approved}</p>
          <p className="text-sm text-text-secondary">Approved</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <p className="text-2xl font-bold text-yellow-500">{stats.pending}</p>
          <p className="text-sm text-text-secondary">Pending</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <p className="text-2xl font-bold text-red-500">{stats.rejected}</p>
          <p className="text-sm text-text-secondary">Rejected</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4">
          <p className="text-2xl font-bold text-accent">{stats.totalDownloads.toLocaleString()}</p>
          <p className="text-sm text-text-secondary">Total Downloads</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-bg-card border border-border-default rounded-xl p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search extensions..."
              className="w-full pl-10 pr-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
          >
            <option value="all">All Status</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Extensions Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredExtensions.map((extension) => (
          <div
            key={extension.id}
            className={cn(
              'bg-bg-card border border-border-default rounded-xl p-5 hover:border-accent/50 transition-colors cursor-pointer',
              extension.status === 'pending' && 'border-yellow-500/30'
            )}
            onClick={() => setSelectedExtension(extension)}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent/20 to-purple-500/20 flex items-center justify-center flex-shrink-0">
                <Package className="text-accent" size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div>
                    <h3 className="font-semibold text-text-primary truncate">{extension.name}</h3>
                    <p className="text-xs text-text-tertiary">{extension.publisher} &middot; v{extension.version}</p>
                  </div>
                  {getStatusBadge(extension.status)}
                </div>
                <p className="text-sm text-text-secondary line-clamp-2 mb-3">{extension.description}</p>
                <div className="flex items-center gap-4 text-xs text-text-tertiary">
                  <span className="flex items-center gap-1">
                    <Download size={12} />
                    {extension.downloads.toLocaleString()}
                  </span>
                  {extension.rating > 0 && (
                    <span className="flex items-center gap-1">
                      <Star size={12} className="text-yellow-500" />
                      {extension.rating}
                    </span>
                  )}
                  <span className="px-2 py-0.5 rounded bg-bg-elevated">{extension.category}</span>
                </div>
              </div>
            </div>

            {extension.status === 'pending' && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border-default">
                <button
                  onClick={(e) => { e.stopPropagation(); handleApprove(extension.id); }}
                  disabled={isUpdating}
                  className="flex-1 btn btn-sm bg-green-500/10 text-green-500 hover:bg-green-500/20 disabled:opacity-50"
                >
                  <ThumbsUp size={14} />
                  Approve
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleReject(extension.id); }}
                  disabled={isUpdating}
                  className="flex-1 btn btn-sm bg-red-500/10 text-red-500 hover:bg-red-500/20 disabled:opacity-50"
                >
                  <ThumbsDown size={14} />
                  Reject
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedExtension(extension); }}
                  className="btn btn-sm btn-secondary"
                >
                  <Eye size={14} />
                  Review
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {filteredExtensions.length === 0 && (
        <div className="text-center py-12">
          <Package className="mx-auto mb-4 text-text-tertiary" size={48} />
          <p className="text-text-secondary">No extensions found</p>
        </div>
      )}

      {/* Extension Detail Modal */}
      {selectedExtension && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-bg-card border border-border-default rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border-default">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-accent/20 to-purple-500/20 flex items-center justify-center">
                    <Package className="text-accent" size={32} />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">{selectedExtension.name}</h2>
                    <p className="text-text-secondary">{selectedExtension.publisher} &middot; v{selectedExtension.version}</p>
                  </div>
                </div>
                <button onClick={() => setSelectedExtension(null)} className="p-1 hover:bg-bg-elevated rounded-lg">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-2">
                {getStatusBadge(selectedExtension.status)}
                <span className="px-2 py-0.5 rounded bg-bg-elevated text-xs">{selectedExtension.category}</span>
              </div>

              <div>
                <h3 className="font-medium text-text-primary mb-2">Description</h3>
                <p className="text-text-secondary">{selectedExtension.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-bg-elevated rounded-lg p-4">
                  <p className="text-2xl font-bold text-text-primary">{selectedExtension.downloads.toLocaleString()}</p>
                  <p className="text-sm text-text-secondary">Downloads</p>
                </div>
                <div className="bg-bg-elevated rounded-lg p-4">
                  <p className="text-2xl font-bold text-text-primary flex items-center gap-1">
                    {selectedExtension.rating > 0 ? selectedExtension.rating : 'N/A'}
                    {selectedExtension.rating > 0 && <Star size={16} className="text-yellow-500" />}
                  </p>
                  <p className="text-sm text-text-secondary">Rating ({selectedExtension.ratingCount} reviews)</p>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-text-primary mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {(selectedExtension.tags || []).map(tag => (
                    <span key={tag} className="px-2 py-1 rounded bg-bg-elevated text-xs">{tag}</span>
                  ))}
                  {(!selectedExtension.tags || selectedExtension.tags.length === 0) && (
                    <span className="text-text-tertiary text-xs">No tags</span>
                  )}
                </div>
              </div>

              <div>
                <h3 className="font-medium text-text-primary mb-2">Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-2 border-b border-border-default">
                    <span className="text-text-secondary">Submitted</span>
                    <span className="text-text-primary">{new Date(selectedExtension.submittedAt).toLocaleDateString()}</span>
                  </div>
                  {selectedExtension.reviewedAt && (
                    <div className="flex justify-between py-2 border-b border-border-default">
                      <span className="text-text-secondary">Reviewed</span>
                      <span className="text-text-primary">{new Date(selectedExtension.reviewedAt).toLocaleDateString()}</span>
                    </div>
                  )}
                  {selectedExtension.reviewedBy && (
                    <div className="flex justify-between py-2 border-b border-border-default">
                      <span className="text-text-secondary">Reviewed By</span>
                      <span className="text-text-primary">{selectedExtension.reviewedBy}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-2">
                    <span className="text-text-secondary">Extension ID</span>
                    <code className="text-text-primary text-xs bg-bg-elevated px-2 py-1 rounded">{selectedExtension.id}</code>
                  </div>
                </div>
              </div>

              {selectedExtension.status === 'pending' && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="text-yellow-500 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="font-medium text-yellow-500">Pending Review</p>
                      <p className="text-sm text-yellow-500/70">This extension is waiting for approval.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-border-default flex justify-between">
              <div className="flex gap-2">
                <button
                  onClick={() => handleDelete(selectedExtension.id)}
                  disabled={isUpdating}
                  className="btn bg-red-500/10 text-red-500 hover:bg-red-500/20 disabled:opacity-50"
                >
                  {isUpdating ? <Loader2 size={16} className="animate-spin" /> : 'Delete'}
                </button>
                <button
                  onClick={() => {
                    openEditModal(selectedExtension);
                    setSelectedExtension(null);
                  }}
                  disabled={isUpdating}
                  className="btn bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 disabled:opacity-50"
                >
                  <Edit size={16} />
                  Edit / Update
                </button>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setSelectedExtension(null)} className="btn btn-secondary">
                  Close
                </button>
                {selectedExtension.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleReject(selectedExtension.id)}
                      disabled={isUpdating}
                      className="btn bg-red-500/10 text-red-500 hover:bg-red-500/20 disabled:opacity-50"
                    >
                      {isUpdating ? <Loader2 size={16} className="animate-spin" /> : <ThumbsDown size={16} />}
                      Reject
                    </button>
                    <button
                      onClick={() => handleApprove(selectedExtension.id)}
                      disabled={isUpdating}
                      className="btn btn-primary disabled:opacity-50"
                    >
                      {isUpdating ? <Loader2 size={16} className="animate-spin" /> : <ThumbsUp size={16} />}
                      Approve
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Extension Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-bg-card border border-border-default rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-border-default flex items-center justify-between">
              <h2 className="text-xl font-semibold">Add Extension</h2>
              <button onClick={() => { setShowAddModal(false); resetForm(); }} className="p-1 hover:bg-bg-elevated rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Extension Name</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="My Extension"
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Publisher</label>
                  <input
                    type="text"
                    value={formPublisher}
                    onChange={(e) => setFormPublisher(e.target.value)}
                    placeholder="Publisher Name"
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Version</label>
                  <input
                    type="text"
                    value={formVersion}
                    onChange={(e) => setFormVersion(e.target.value)}
                    placeholder="1.0.0"
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Extension description..."
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <select
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value as ExtensionCategory)}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select
                    value={formStatus}
                    onChange={(e) => setFormStatus(e.target.value as ExtensionStatus)}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tags (comma separated)</label>
                <input
                  type="text"
                  value={formTags}
                  onChange={(e) => setFormTags(e.target.value)}
                  placeholder="tag1, tag2, tag3"
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
            </div>
            <div className="p-6 border-t border-border-default flex justify-end gap-3">
              <button onClick={() => { setShowAddModal(false); resetForm(); }} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleAddExtension}
                disabled={!formName || !formPublisher || !formDescription || isUpdating}
                className="btn btn-primary disabled:opacity-50"
              >
                {isUpdating ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Add Extension
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Extension Modal */}
      {showEditModal && editingExtension && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-bg-card border border-border-default rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border-default flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Upload className="text-blue-500" size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Update Extension</h2>
                  <p className="text-sm text-text-secondary">Version {editingExtension.version} → {formVersion}</p>
                </div>
              </div>
              <button onClick={() => { setShowEditModal(false); resetForm(); }} className="p-1 hover:bg-bg-elevated rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Extension Name</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Publisher</label>
                  <input
                    type="text"
                    value={formPublisher}
                    onChange={(e) => setFormPublisher(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Version</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formVersion}
                      onChange={(e) => setFormVersion(e.target.value)}
                      className="flex-1 px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                    />
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => incrementVersion('patch')}
                        className="px-2 py-1 text-xs bg-green-500/10 text-green-500 hover:bg-green-500/20 rounded"
                        title="Increment Patch (bug fixes)"
                      >
                        +0.0.1
                      </button>
                      <button
                        type="button"
                        onClick={() => incrementVersion('minor')}
                        className="px-2 py-1 text-xs bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 rounded"
                        title="Increment Minor (new features)"
                      >
                        +0.1.0
                      </button>
                      <button
                        type="button"
                        onClick={() => incrementVersion('major')}
                        className="px-2 py-1 text-xs bg-purple-500/10 text-purple-500 hover:bg-purple-500/20 rounded"
                        title="Increment Major (breaking changes)"
                      >
                        +1.0.0
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Changelog (What's new in this version)</label>
                <textarea
                  value={formChangelog}
                  onChange={(e) => setFormChangelog(e.target.value)}
                  rows={4}
                  placeholder="• Bug fixes&#10;• Performance improvements&#10;• New features..."
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none resize-none font-mono text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <select
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value as ExtensionCategory)}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select
                    value={formStatus}
                    onChange={(e) => setFormStatus(e.target.value as ExtensionStatus)}
                    className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tags (comma separated)</label>
                <input
                  type="text"
                  value={formTags}
                  onChange={(e) => setFormTags(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>

              {/* Update Summary */}
              {formVersion !== editingExtension.version && (
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Upload className="text-blue-500 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="font-medium text-blue-500">Version Update</p>
                      <p className="text-sm text-blue-500/70">
                        Updating from v{editingExtension.version} to v{formVersion}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-border-default flex justify-end gap-3">
              <button onClick={() => { setShowEditModal(false); resetForm(); }} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleUpdateExtension}
                disabled={!formName || !formPublisher || !formDescription || isUpdating}
                className="btn btn-primary"
              >
                {isUpdating ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    Update Extension
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
